import { businessType, jobPositions } from '@forwardflow/helpers/utils'
import { states } from '@forwardflow/helpers/utils/states'
import { IBusinessDetails } from '@forwardflow/types/onboarding'

export type TBusinessField = keyof IBusinessDetails

export interface IBusinessFieldDef {
   name: TBusinessField
   label: string
   placeholder?: string
   kind?: 'text' | 'select' | 'email' | 'tel'
   options?: { label: string; value: string }[]
   optional?: boolean
   hint?: string
}

export interface IBusinessSection {
   id: 'business' | 'representative' | 'address'
   title: string
   blurb: string
   fields: IBusinessFieldDef[]
}

/**
 * The texting (A2P) registration form the carriers need, in the three
 * groups a person thinks in. Field names are the record's
 * `businessDetails` names, which `BusinessService.formatBusinessDetails`
 * turns into the registration payload.
 */
export const BUSINESS_SECTIONS: IBusinessSection[] = [
   {
      id: 'business',
      title: 'The business',
      blurb: 'As registered with the state.',
      fields: [
         {
            name: 'legalBusinessName',
            label: 'Legal business name',
            placeholder: 'As registered',
         },
         {
            name: 'friendlyBusinessName',
            label: 'Name leads will see',
            placeholder: 'Your brand name',
         },
         { name: 'websiteUrl', label: 'Website', placeholder: 'https://' },
         {
            name: 'businessType',
            label: 'Business type',
            kind: 'select',
            options: businessType,
         },
         {
            name: 'businessRegistrationNumber',
            label: 'EIN (tax ID)',
            placeholder: '12-3456789',
         },
         {
            name: 'socialMediaProfileUrls',
            label: 'Social media profile',
            placeholder: 'A public page helps approval',
            optional: true,
         },
      ],
   },
   {
      id: 'representative',
      title: 'The person behind it',
      blurb: 'The authorised representative the carriers can contact. Not shown to leads.',
      fields: [
         { name: 'authorizedRepresentativeFirstName', label: 'First name' },
         { name: 'authorizedRepresentativeLastName', label: 'Last name' },
         {
            name: 'authorizedRepresentativeJobPosition',
            label: 'Job position',
            kind: 'select',
            options: jobPositions,
         },
         {
            name: 'email',
            label: 'Business email',
            kind: 'email',
            placeholder: 'you@brokerage.com',
         },
         {
            name: 'phoneNumber',
            label: 'Mobile number',
            kind: 'tel',
            placeholder: '(719) 555-0100',
         },
      ],
   },
   {
      id: 'address',
      title: 'The registered address',
      blurb: 'Matched against public records.',
      fields: [
         { name: 'street', label: 'Street address' },
         { name: 'city', label: 'City' },
         { name: 'region', label: 'State', kind: 'select', options: states },
         { name: 'postalCode', label: 'ZIP', placeholder: '5 digits' },
      ],
   },
]

export const BUSINESS_FIELDS: IBusinessFieldDef[] = BUSINESS_SECTIONS.flatMap(
   (s) => s.fields
)

export const validateBusinessField = (
   name: TBusinessField,
   value: string | undefined,
   optional?: boolean
): string | null => {
   const v = (value || '').trim()
   if (!v) return optional ? null : 'Needed for approval'
   if (name === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v))
      return 'Enter a valid email'
   if (name === 'phoneNumber' && v.replace(/\D/g, '').length < 10)
      return 'Enter a 10-digit US number'
   if (
      name === 'businessRegistrationNumber' &&
      v.replace(/\D/g, '').length !== 9
   )
      return 'An EIN has 9 digits'
   if (name === 'postalCode' && !/^\d{5}$/.test(v)) return 'ZIP is 5 digits'
   if (
      (name === 'websiteUrl' || name === 'socialMediaProfileUrls') &&
      !/^(https?:\/\/)?([\w-]+\.)+[\w-]+/.test(v)
   )
      return 'Enter a web address'
   return null
}
